import { datadogRum } from "@datadog/browser-rum";
import shallow from "zustand/shallow";

import { ENVIRONMENTS } from "@consts";
import { QueryEventTypes } from "src/pages/Conversations/ConversationsConsts";
import useAppStore from "src/stores/appStore";

const REDACTED = "REDACTED";
const FREE_TEXT_EVENT_TYPES = [QueryEventTypes.USER_UTTERANCE.key, QueryEventTypes.BOT_UTTERANCE.key];

const redactFreeTextFilters = rawFilters => {
  try {
    const filters = JSON.parse(rawFilters);
    if (!Array.isArray(filters)) return REDACTED;

    return JSON.stringify(filters.map(filter =>
      FREE_TEXT_EVENT_TYPES.includes(filter?.eventType) ? { ...filter, value: REDACTED } : filter));
  } catch {
    return REDACTED;
  }
};

const scrubUrl = url => {
  try {
    const parsedUrl = new URL(url);
    if (parsedUrl.searchParams.has("searchText")) parsedUrl.searchParams.set("searchText", REDACTED);
    if (parsedUrl.searchParams.has("filters")) {
      parsedUrl.searchParams.set("filters", redactFreeTextFilters(parsedUrl.searchParams.get("filters")));
    }

    return parsedUrl.toString();
  } catch {
    return url;
  }
};

const scrubConversationSearchTerms = event => {
  if (event.view?.url) event.view.url = scrubUrl(event.view.url);
  if (event.view?.referrer) event.view.referrer = scrubUrl(event.view.referrer);

  return true;
};

const getRumGlobalContext = state => ({
  userId: state.userPermissions?.userId,
  accountId: state.selectedAccountId,
  assistantId: state.selectedAssistant?.assistantId,
  assistantName: state.selectedAssistant?.assistantName,
  assistantChannel: state.selectedAssistant?.channel,
  assistantFolder: state.selectedAssistant?.assistantFolder
});

let unsubscribeRumContextSync;

const syncRumGlobalContext = () => {
  unsubscribeRumContextSync?.();
  unsubscribeRumContextSync = useAppStore.subscribe(getRumGlobalContext, context => datadogRum.setGlobalContext(context), {
    equalityFn: shallow,
    fireImmediately: true
  });
};

const initDatadog = () => {
  const datadogEnvironment = import.meta.env.VITE_ENV === ENVIRONMENTS.BRANCH
    ? ENVIRONMENTS.DEVELOPMENT
    : import.meta.env.VITE_ENV;

  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_DASHBOARD_RUM_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_DASHBOARD_RUM_CLIENT_TOKEN,
    site: "datadoghq.com",
    service: import.meta.env.VITE_DATADOG_DASHBOARD_RUM_SERVICE_NAME,
    env: datadogEnvironment,
    version: import.meta.env.VITE_CI_COMMIT_SHORT_SHA,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    defaultPrivacyLevel: "allow",
    allowedTracingUrls: [import.meta.env.VITE_DASHBOARD_API_URL],
    traceSampleRate: 100,
    beforeSend: scrubConversationSearchTerms
  });

  datadogRum.startSessionReplayRecording();
  syncRumGlobalContext();
};

export default initDatadog;
